import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useResourceKit, useUrlState } from '@resource-kit/core/react';
import type { AnyFieldDef, Condition, FilterPreset, OperatorId } from '@resource-kit/model';
import { useDataTableParams, type DataTableParams } from './useDataTableParams';
import { resourceKeys } from '../query/keys';
import { droppedFilterFields } from '../list/whitelist';
import { INTERNAL_QUERY_KEYS } from '../query/serialize';
import { deriveColumnTypes } from '../query/deriveSchema';
import { mergeConditionRows, type ConditionMergeNotice } from '../query/domain';
import type { ListResult, ListStatus } from '../list/types';
import { applyClientQuery, CLIENT_PAGINATION_CAP } from '../list/clientPagination';
import { fetchAllRowsServer } from '../list/exportAll';
import {
  resolveListCapabilities,
  type ListCapabilities,
  type ListSource,
  type ResolvedListCapabilities,
} from '../list/listCapabilities';
import { DEFAULT_STALE_TIME, type HostFilterPreset, type ResourceBundle } from './bundle';
import { useToday } from './useToday';

/**
 * What `<ListView>` renders its pager from. NOT the wire shape: the `/api`
 * list face answers `ListResult` (`{ items, total, page, limit }`) and never
 * carries `totalPages`, so every source — the wire and the two in-memory ones
 * that have no wire at all — derives it inside this module family.
 */
export interface ListPagination {
  total: number;
  totalPages: number;
  page: number;
  limit: number;
}

/**
 * Everything a list view needs, and nothing it renders — the ONE contract all
 * three sources satisfy (#1803, design §6.6).
 *
 * `useRemoteList` / bulk `useUrlOwnedList` / `useLocalList` differ in who owns the query
 * state and where the rows come from; they agree on this shape, which is why
 * there is exactly one `<ListView>` instead of one per source.
 */
export interface ListController<T> {
  /** Which source produced this controller. Decides who owns the state. */
  source: ListSource;
  /** The resolved capability set (design §6.6 defaults + the view's opt-ins). */
  capabilities: ResolvedListCapabilities;

  // ── Query state ────────────────────────────────────────────────────────
  page: number;
  limit: number;
  search: string;
  conditions: Condition[];
  sortField: string;
  sortOrder: 'asc' | 'desc';
  /** The resolved request the list adapter is called with. */
  params: DataTableParams;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSort: (field: string) => void;
  setSearch: (search: string) => void;
  /** Replace the whole condition set (clears the active preset marker). */
  setConditions: (conditions: Condition[]) => void;
  /** Upsert (value set) or remove (value null) one field's condition. */
  setFieldCondition: (
    field: string,
    op: OperatorId,
    value: string | [string, string] | null,
  ) => void;
  /** True when search or any condition is narrowing the list. */
  isFiltered: boolean;
  /** Drop search + every condition in ONE transition. */
  clearFilters: () => void;

  // ── Presets ────────────────────────────────────────────────────────────
  presets: readonly HostFilterPreset[] | undefined;
  activePresetId: string | null;
  togglePreset: (preset: FilterPreset) => void;

  // ── Data ───────────────────────────────────────────────────────────────
  rows: T[];
  pagination: ListPagination;
  status: ListStatus;
  error: string | null;
  /** Evict the cached rows and re-attempt. */
  retry: () => void;
  /** Filter fields the backend whitelist would drop. */
  droppedFilters: string[];
  /**
   * Same-field condition collapses (design §5.9). Non-empty means two or more
   * rows targeted one field and were combined or overridden — announced,
   * never silently dropped.
   */
  mergeNotices: readonly ConditionMergeNotice[];
  /** Resolve the FULL row set for the current query (CSV export). */
  exportAll: () => Promise<T[]>;
}

// Stable empty-rows reference: keeps the `rows` prop identity stable across
// renders while the query hasn't loaded yet.
const EMPTY_ROWS: never[] = [];

const EMPTY_PAGINATION: ListPagination = { page: 1, limit: 10, total: 0, totalPages: 0 };

/**
 * Derives `<ListView>`'s pagination from a REMOTE page (#1909).
 *
 * Not a translation point: by the time a page reaches here the page-size key
 * is already `limit`, settled at the adapter boundary — by `toListResult`
 * (`services/list-envelope.ts`) for `ListPage` responses, and by the
 * hand-written adapters that build a `ListResult` field by field. The outer
 * `{ data }` wrapper comes off earlier still, in `http.get`. This function's
 * parameter is the resulting `ListResult`.
 *
 * `ListResult` carries `limit` and no `totalPages`; `<ListView>` pages on
 * `limit` + `totalPages`. The divide is guarded on `limit > 0` because a
 * page size of zero would otherwise divide to `Infinity` and render an
 * unbounded pager. The guard is on THIS remote path specifically: only a
 * server-supplied page size can be one the frontend did not choose.
 *
 * The two in-memory divides are unguarded for DIFFERENT reasons — NOT because
 * "the limit is clamped everywhere":
 *   - the bulk branch below divides by `useDataTableParams`'s `limit`, whose
 *     URL-PARSED value is clamped positive — that hook's `limit` memo
 *     (`packages/resource-kit-controllers/react/useDataTableParams.ts::useDataTableParams`).
 *     Its `initialConfig?.limit ?? DEFAULT_LIMIT` fallback — the same hook's
 *     `initialLimit` — is NOT clamped, `??`
 *     does not catch `0`, so a `host.defaultLimit` of 0 would reach the divide.
 *     Both configured values are 100 today, so no live bug.
 *   - `useLocalList` has no clamp at all: it holds its own `useState` limit
 *     (`packages/resource-kit-controllers/react/useLocalList.ts::useLocalList`)
 *     and its `setLimit` writes through unclamped,
 *     pointedly unlike the `setPage` beside it. What protects it is that the
 *     only limits it ever holds are its own `DEFAULT_LIMIT` of 10 and the
 *     pager's `CLIENT_PAGE_SIZES`; no caller passes `options.limit`. Add a
 *     guard there if one ever passes it from data.
 */
function toListPagination(page: ListResult<unknown> | undefined): ListPagination {
  if (page === undefined) return EMPTY_PAGINATION;
  return {
    page: page.page,
    limit: page.limit,
    total: page.total,
    totalPages: page.limit > 0 ? Math.ceil(page.total / page.limit) : 0,
  };
}

function legacySortString(
  defaultSort: { field: string; dir: 'asc' | 'desc' } | undefined,
): string | undefined {
  return defaultSort === undefined ? undefined : `${defaultSort.field}:${defaultSort.dir}`;
}

export interface UrlOwnedListOptions {
  /** `remote` or `bulk` — both own URL state; `local` has its own hook. */
  source: Exclude<ListSource, 'local'>;
  /** Per-capability opt-in. Anything omitted takes the source default. */
  capabilities?: ListCapabilities;
  /**
   * The fields free-text search may match in memory. DECLARED, never derived
   * from every field — see the fallback note at the use site.
   */
  searchFields?: readonly string[];
}

/**
 * The URL-owned list controller shared by `remote` and `bulk`.
 *
 * Both sources put page / limit / sort / search / conditions / preset in the
 * URL and persist them per resource; they differ only in HOW rows arrive —
 * `remote` asks the server for one page at a time, `bulk` pulls the capped set
 * once and runs every operation in memory. That difference is a fetch strategy,
 * not a state-ownership boundary, which is why they share this body.
 */
export function useUrlOwnedList<T, F extends Record<string, AnyFieldDef>>(
  bundle: ResourceBundle<T, F>,
  options: UrlOwnedListOptions,
): ListController<T> {
  const { t } = useResourceKit();
  const queryClient = useQueryClient();
  const { source } = options;
  const { list, host } = bundle;
  const searchable = list.capabilities?.search;
  const defaultSort = legacySortString(list.defaultSort);

  // Stale-preset fix: hand `useDataTableParams` a preset resolver so any
  // hydration path (localStorage restore or a bookmarked `?_preset=<id>`)
  // RE-RUNS the preset's `buildConditions(today)` instead of restoring the
  // absolute date it resolved to on a previous visit — keeping relative
  // presets ("today"/"tomorrow") fresh — and drops an unknown `_preset` so no
  // ghost state lingers. Keyed on `host.slug` (a stable primitive identity for
  // the module-level bundle) and on `today`: when the operating-zone calendar
  // day changes (#2780) the resolver changes identity, so the active preset
  // re-runs with the new day and the list requests again.
  const today = useToday();
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const resolvePresetConditions = useMemo(
    () =>
      (presetId: string): Condition[] | null => {
        const preset = host.presets?.find((p) => p.id === presetId);
        return preset ? preset.buildConditions(today) : null;
      },
    [host.slug, today],
  );

  // #2094: the field-id → column-type map the serializer needs to complete a
  // temporal bound. Derived from the MODEL (not from the view's ColumnSchema,
  // which is built below this hook) so the wire form is decided by the same
  // declaration the QueryBuilder offers the operator its editor from.
  const columnTypes = useMemo(() => deriveColumnTypes(bundle.model.fields), [bundle.model.fields]);

  const {
    page,
    limit,
    search,
    conditions,
    params,
    setPage,
    setLimit,
    setSort,
    setSearch,
    onConditionsChange,
    sortField,
    sortOrder,
  } = useDataTableParams({
    sort: defaultSort,
    // #1419: optional initial page size (unset ⇒ the hook's DEFAULT_LIMIT=10).
    limit: host.defaultLimit,
    defaultConditions: host.defaultConditions,
    persistKey: host.slug,
    // #1231 / #1639 / #1642: hand the backend whitelists to the persistence
    // layer so a stale stored filter / sort / needle is dropped on restore
    // instead of pinning a permanent "filters dropped" banner, a 400, or a
    // falsely-empty list.
    filterable: host.filterable,
    sortable: host.sortable,
    // #1642 / #1803 risk 1: `?? false` is load-bearing. A resource that
    // never declared searchability must NOT be handed `true` — the list
    // kernel answers 400 for a `search` param on a route with no searchable
    // columns, and one flipped default 400s production lists en masse.
    searchable: searchable ?? false,
    resolvePresetConditions,
    // #2094: the filter date pickers emit bare days (#2111 — single-bound as
    // well as range), and URLs written before #2111 still carry `…THH:mm`;
    // the list kernel accepts only `YYYY-MM-DDTHH:MM:SS.sssZ` on an instant
    // column and 400s everything else. This map is what lets the serializer
    // tell the two temporal column kinds apart.
    columnTypes,
  });

  // ─── Capabilities (design §6.6) ────────────────────────────────────────
  //
  // Resolved here, not in the view: the source decides the defaults, and the
  // source is what this hook IS. The view opts individual capabilities in or
  // out without changing the component contract.
  const requestedCapabilities = useMemo(
    () => ({ ...list.capabilities, ...options.capabilities }),
    [list.capabilities, options.capabilities],
  );
  const capabilities = useMemo(
    () =>
      resolveListCapabilities(source, requestedCapabilities, {
        searchable,
        filterable: host.filterable,
        sortable: host.sortable,
        selectable: list.capabilities?.select,
        hasRowActions: (host.rowActions?.length ?? 0) > 0,
      }),
    [
      source,
      requestedCapabilities,
      searchable,
      host.filterable,
      host.sortable,
      list.capabilities?.select,
      host.rowActions,
    ],
  );

  // ─── Preset toggle state (#575 + #590) ─────────────────────────────────
  //
  // The "active preset" is presentation state (which button to highlight).
  // It is NOT derived from the shape of `conditions`, because a hand-built
  // advanced filter could accidentally match a preset's predicate while
  // being semantically not "the preset".
  //
  // It lives in the URL as `?_preset=<id>` so a refresh / shared link
  // restores the highlight alongside the conditions. The `_` prefix keeps it
  // out of the condition namespace.
  const { params: urlParams } = useUrlState();
  const activePresetId = urlParams.get(INTERNAL_QUERY_KEYS.preset);
  const presets = host.presets;
  const hasPresets = presets != null && presets.length > 0;

  // Every manual condition change drops the active-preset marker, keeping the
  // visual signal honest: once the operator touches the filter, no preset is
  // "active" anymore. Preset writes must update BOTH `_preset` and the
  // condition keys in ONE URL transition — `setSearchParams` goes through
  // `navigate()`, not batched useState, so two synchronous calls would each
  // read the same snapshot and the second would clobber the first.
  const setConditions = useCallback(
    (next: Condition[]) => {
      if (hasPresets) {
        onConditionsChange(next, { [INTERNAL_QUERY_KEYS.preset]: null });
      } else {
        onConditionsChange(next);
      }
    },
    [hasPresets, onConditionsChange],
  );

  const togglePreset = useCallback(
    (preset: FilterPreset) => {
      if (activePresetId === preset.id) {
        // Toggle off — back to the resource's default conditions.
        onConditionsChange(host.defaultConditions ?? [], {
          [INTERNAL_QUERY_KEYS.preset]: null,
        });
      } else {
        onConditionsChange(preset.buildConditions(today), {
          [INTERNAL_QUERY_KEYS.preset]: preset.id,
        });
      }
    },
    [activePresetId, host.defaultConditions, onConditionsChange, today],
  );

  // Upsert / remove one field's condition, preserving every other active
  // condition. This is how quick filters write into the shared `conditions`
  // state; the URL transition resets page to 1.
  const setFieldCondition = useCallback(
    (field: string, op: OperatorId, value: string | [string, string] | null) => {
      const next = conditions.filter((c) => c.field !== field);
      if (value !== null) {
        next.push({ id: `${field}::${op}`, field, op, value });
      }
      setConditions(next);
    },
    [conditions, setConditions],
  );

  // ─── Day roll resets the page of an active preset (#2780) ──────────────
  //
  // When `today` changes, the active preset re-runs with the new day (the
  // `resolvePresetConditions` memo above), so the old page index points into a
  // different result set: the operator on page N of "today" would land on an
  // out-of-range empty page after midnight. Go back to page 1, as every other
  // condition change does through the URL writers. The rule is the day changed
  // AND a declared preset is active AND page > 1. It does not ask whether the
  // preset's conditions depend on the day: resetting the page of a
  // day-independent preset is harmless.
  //
  // `prevDayRef` holds the last day whose page is settled. It starts at the
  // mount day, so neither mount nor hydration counts as a roll. While a reset
  // is due, the ref stays on the old day: renders that run before the URL
  // write lands (a query update, for one) still see the roll and still use
  // page 1, so the new day never goes out with the stale page. Once the URL
  // carries page 1, `page > 1` is false, no reset is due, and the effect moves
  // the ref to `today`; so the write happens once and nothing loops.
  const prevDayRef = useRef(today);
  const prevDay = prevDayRef.current;
  const resetPageForDayRoll =
    prevDay !== today &&
    page > 1 &&
    activePresetId !== null &&
    host.presets?.some((p) => p.id === activePresetId) === true;
  const effectivePage = resetPageForDayRoll ? 1 : page;
  const effectiveParams = useMemo(
    () => (resetPageForDayRoll ? { ...params, page: 1 } : params),
    [resetPageForDayRoll, params],
  );
  useEffect(() => {
    // Writes ONLY `page`, in the replace form `setPage` always uses. `_preset`
    // and the stale condition keys in the URL stay as they are (the reader
    // re-derives conditions from the preset).
    //
    // `page` is a dependency so that a page write that lands while the reset
    // is due re-runs this effect and converges back to 1, whatever `setPage`
    // identity the UrlState adapter gives.
    if (resetPageForDayRoll) setPage(1);
    else prevDayRef.current = today;
  }, [today, resetPageForDayRoll, page, setPage]);

  const isFiltered = search !== '' || conditions.length > 0;

  // One transition, not two: search and conditions both live in the URL, and
  // clearing them in separate calls would let the second clobber the first.
  const clearFilters = useCallback(() => {
    onConditionsChange([], { [INTERNAL_QUERY_KEYS.preset]: null, search: null });
  }, [onConditionsChange]);

  // ─── Same-field merge notices (#1803, design §5.9) ─────────────────────
  //
  // `serializeConditionsToParams` collapses same-field rows through the very
  // same `mergeConditionRows`, so this reports exactly what the request and
  // the URL did — not a second opinion about it.
  const mergeNotices = useMemo(() => mergeConditionRows(conditions).notices, [conditions]);

  // ─── Dropped-filter warning (#1154) ────────────────────────────────────
  //
  // Cross-check the active conditions against the resource's declared backend
  // whitelist. Any field/op the `list` adapter would drop means the operator
  // sees an unfiltered (or under-filtered) list while the chip / preset claims
  // the filter is applied — a silent "false filtered view".
  const droppedFilters = useMemo(
    () => droppedFilterFields(params.extraParams, host.filterable),
    [params.extraParams, host.filterable],
  );
  const droppedFilterList = droppedFilters.join(', ');
  useEffect(() => {
    if (droppedFilterList !== '') {
      console.warn(
        `[useUrlOwnedList:${host.slug}] active filters not accepted by the backend whitelist and dropped: ${droppedFilterList}`,
      );
    }
  }, [droppedFilterList, host.slug]);

  const keys = useMemo(() => resourceKeys(host.slug), [host.slug]);

  // Retry recovery (#1157). When a render error is rooted in the fetched rows
  // themselves (a cell renderer throwing on a bad record), a retry must
  // re-attempt with FRESH data. Two things are required and neither alone is
  // enough:
  //   1. Evict the cached rows — an `invalidateQueries` would only mark them
  //      stale, so the very next render re-reads the same bad rows.
  //   2. Suppress `keepPreviousData` for that first post-retry render. The
  //      placeholder keeps surfacing the last-resolved (bad) rows even after
  //      the cache is evicted, so without this the child re-renders the poison
  //      and re-throws before the refetch can land.
  const [isRetrying, setIsRetrying] = useState(false);

  // Bulk fetching (#1392 D5, generalised by #1803). In `bulk` mode the whole
  // capped set is fetched ONCE with a stable request/key (independent of page
  // / sort / search / filter) and all list operations run in memory below;
  // `remote` is the server-paginated path — the request tracks the URL params.
  const bulkMode = source === 'bulk';
  const fetchParams = useMemo(
    () =>
      bulkMode
        ? {
            page: 1,
            limit: CLIENT_PAGINATION_CAP,
            sort: defaultSort ?? 'created_at:desc',
          }
        : effectiveParams,
    [bulkMode, effectiveParams, defaultSort],
  );

  const query = useQuery({
    queryKey: keys.list(fetchParams),
    queryFn: () => host.api.list(fetchParams),
    placeholderData: isRetrying ? undefined : keepPreviousData,
    staleTime: host.staleTime ?? DEFAULT_STALE_TIME,
    // #1416: optional background refetch cadence (unset ⇒ no interval); a
    // backgrounded tab stays quiet (refetchIntervalInBackground default false).
    refetchInterval: host.refetchInterval,
  });

  // Once the post-retry refetch resolves, restore normal keep-previous
  // behavior so pagination transitions no longer flash a loading state.
  useEffect(() => {
    if (isRetrying && query.isSuccess) setIsRetrying(false);
  }, [isRetrying, query.isSuccess, query.dataUpdatedAt]);

  const fetchedRows = query.data?.items ?? EMPTY_ROWS;

  // Field keys eligible for in-memory free-text search.
  //
  // §6.6 requires this to be DECLARED, not derived from every field — a
  // derived set makes free-text search hit columns nobody meant to expose.
  // Explicit bulk callers and `useLocalList` therefore REQUIRE it. The
  // list-items fallback survives for client-paginated definitions that never
  // declared one; those converge as they migrate.
  const declaredSearchFields = options.searchFields ?? list.searchFields;
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const searchFields = useMemo(
    () => declaredSearchFields ?? list.items.map((item) => item.field),
    [host.slug, declaredSearchFields],
  );

  // In bulk mode, run search/filter/sort/paginate over the cached rows; in
  // remote mode pass the fetched page straight through.
  const clientResult = useMemo(
    () =>
      bulkMode
        ? applyClientQuery(fetchedRows, {
            search,
            conditions,
            sortField,
            sortOrder,
            page: effectivePage,
            limit,
            searchFields,
          })
        : null,
    [
      bulkMode,
      fetchedRows,
      search,
      conditions,
      sortField,
      sortOrder,
      effectivePage,
      limit,
      searchFields,
    ],
  );

  const rows = bulkMode ? clientResult!.rows : fetchedRows;
  const pagination: ListPagination = bulkMode
    ? {
        page: effectivePage,
        limit,
        total: clientResult!.total,
        totalPages: Math.ceil(clientResult!.total / limit),
      }
    : toListPagination(query.data);

  // "Export all data" (#1459). Resolves the FULL row set for the operator's
  // current query. In bulk mode the whole capped set is already in memory —
  // run the same search/filter/sort unpaginated. In remote mode walk the list
  // adapter page by page with the current request params, capped so a runaway
  // pull surfaces as an error rather than a locked tab.
  const exportAll = useCallback(async (): Promise<T[]> => {
    if (bulkMode) {
      return applyClientQuery(fetchedRows, {
        search,
        conditions,
        sortField,
        sortOrder,
        page: 1,
        limit: Math.max(fetchedRows.length, 1),
        searchFields,
      }).rows;
    }
    return fetchAllRowsServer(host.api.list, fetchParams, {
      tooLargeError: (cap) => new Error(t('rk.table.exportTooLarge', { limit: cap })),
    });
  }, [
    bulkMode,
    fetchedRows,
    search,
    conditions,
    sortField,
    sortOrder,
    searchFields,
    host.api,
    fetchParams,
    t,
  ]);

  const retry = useCallback(() => {
    queryClient.removeQueries({ queryKey: keys.all() });
    setIsRetrying(true);
  }, [queryClient, keys]);

  // §6.4: the three states are exclusive, and error wins. A failed request
  // that fell through to "ready with zero rows" would render as "no data",
  // which is the single most misleading thing a list can say.
  const status: ListStatus = query.isError ? 'error' : query.isLoading ? 'loading' : 'ready';

  return {
    source,
    capabilities,
    page: effectivePage,
    limit,
    search,
    conditions,
    sortField,
    sortOrder,
    params: effectiveParams,
    setPage,
    setLimit,
    setSort,
    setSearch,
    setConditions,
    setFieldCondition,
    isFiltered,
    clearFilters,
    presets,
    activePresetId,
    togglePreset,
    rows,
    pagination,
    status,
    error: query.error?.message ?? null,
    retry,
    droppedFilters,
    mergeNotices,
    exportAll,
  };
}
